import {
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction
} from 'react';

import type {
  AssistantMessage,
  ChatGPTConversationIdentity,
  DetectedScript
} from '@kaizen/shared';

import {
  isSupabaseConfigured,
  supabase
} from '../lib/supabase';


interface MirrorHistoryRow {
  id: string;
  message_key: string;
  role: 'user' | 'assistant';
  content: string;
  detected_scripts: unknown;
  observed_at: string;
  created_at: string;
}


function normalizedContent(
  value:
    string
): string {

  return value
    .replace(
      /\s+/g,
      ' '
    )
    .trim();
}


function signatureOf(
  role:
    string,

  content:
    string
): string {

  return `${role}:${normalizedContent(content)}`;
}


function normalizeDetectedScripts(
  value:
    unknown
): DetectedScript[] | undefined {

  if (
    !Array.isArray(
      value
    )
  ) {
    return undefined;
  }


  const scripts =
    value.filter(
      (
        item:
          any
      ) =>
        item &&
        typeof item.code ===
          'string' &&
        typeof item.language ===
          'string'
    ) as DetectedScript[];


  return scripts.length >
    0
    ? scripts
    : undefined;
}


export function useJarvisMirrorHistory(
  chat:
    ChatGPTConversationIdentity |
    null |
    undefined,

  setMessages:
    Dispatch<SetStateAction<AssistantMessage[]>>
): void {

  const previousConversationKey =
    useRef<string | null>(
      null
    );


  useEffect(
    () => {

      if (
        !isSupabaseConfigured
      ) {
        return;
      }


      // JARVIS R9 HISTORY STABILITY
      //
      // Never use a temporary generic ChatGPT URL as a
      // conversation history key during R8 reload.
      const conversationKey =
        chat?.conversationId?.trim() ||
        '';


      if (
        !conversationKey
      ) {
        return;
      }


      if (
        previousConversationKey.current !==
          conversationKey
      ) {

        previousConversationKey.current =
          conversationKey;


        setMessages(
          previous =>
            previous.filter(
              message =>
                message.role ===
                'system'
            )
        );
      }


      const historySupabase =
        supabase as any;


      let disposed =
        false;


      const loadHistory =
        async () => {

          const since =
            new Date(
              Date.now() -
              60 *
              60 *
              1000
            ).toISOString();


          try {

            const {
              data,
              error
            } =
              await historySupabase
                .from(
                  'kr_assistant_messages'
                )
                .select(
                  'id,message_key,role,content,detected_scripts,observed_at,created_at'
                )
                .eq(
                  'conversation_key',
                  conversationKey
                )
                .gte(
                  'created_at',
                  since
                )
                .order(
                  'created_at',
                  {
                    ascending:
                      true
                  }
                )
                .limit(
                  500
                );


            if (
              disposed ||
              error ||
              !data
            ) {
              return;
            }


            const rows =
              data as MirrorHistoryRow[];


            const mirrored:
              AssistantMessage[] =
              rows.map(
                row => ({
                  id:
                    `mirror-${row.message_key}`,
                  role:
                    row.role,
                  content:
                    row.content,
                  detectedScripts:
                    normalizeDetectedScripts(
                      row.detected_scripts
                    ),
                  timestamp:
                row.created_at ||
                row.observed_at
                })
              );


            const mirroredSignatures =
              new Set(
                mirrored.map(
                  message =>
                    signatureOf(
                      message.role,
                      message.content
                    )
                )
              );


            const optimisticCutoff =
              Date.now() -
              2 *
              60 *
              1000;


            setMessages(
              previous => {

                const systemMessages =
                  previous.filter(
                    message =>
                      message.role ===
                      'system'
                  );


                const optimistic =
                  previous.filter(
                    message => {

                      if (
                        message.role ===
                          'system' ||
                        message.id.startsWith(
                          'mirror-'
                        )
                      ) {
                        return false;
                      }


                      const timestamp =
                        Date.parse(
                          message.timestamp
                        );


                      if (
                        Number.isFinite(
                          timestamp
                        ) &&
                        timestamp <
                          optimisticCutoff
                      ) {
                        return false;
                      }


                      return !mirroredSignatures.has(
                        signatureOf(
                          message.role,
                          message.content
                        )
                      );
                    }
                  );


                const combined =
                  [
                    ...systemMessages,
                    ...mirrored,
                    ...optimistic
                  ];


                const deduped:
                  AssistantMessage[] =
                  [];


                const seen =
                  new Set<string>();


                for (
                  const message
                  of combined
                ) {

                  const key =
                    message.role ===
                      'system'
                      ? `system:${message.id}`
                      : signatureOf(
                          message.role,
                          message.content
                        );


                  if (
                    seen.has(
                      key
                    )
                  ) {
                    continue;
                  }


                  seen.add(
                    key
                  );


                  deduped.push(
                    message
                  );
                }


                deduped.sort(
                  (
                    a,
                    b
                  ) =>
                    Date.parse(
                      a.timestamp
                    ) -
                    Date.parse(
                      b.timestamp
                    )
                );


                return deduped;
              }
            );

          }
          catch {
            // Migration may not be applied yet during staged rollout.
          }
        };


      void loadHistory();


      const timer =
        window.setInterval(
          () => {
            void loadHistory();
          },
          1500
        );


      return () => {
        disposed =
          true;

        window.clearInterval(
          timer
        );
      };
    },
    [
      chat?.conversationId,
      chat?.url,
      setMessages
    ]
  );
}